// src/pages/dashboard/DashboardPage.tsx
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient'; // Ensure path is correct
import { useAuth } from '@/contexts/AuthContext'; // Ensure path is correct
import { getDateRange, timeRangeOptions, TimeRangeOption } from '@/lib/dateUtils'; // Import date utils
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"; // Import Select
import KPICard from '@/components/dashboard/KPICard';
import OutcomePieChart from '@/components/dashboard/OutcomePieChart';
import ActivityChart from '@/components/dashboard/ActivityChart';
import RecentCallsList from '@/components/dashboard/RecentCallsList';
import DashboardSkeleton from '@/components/dashboard/DashboardSkeleton'; // Import Skeleton
import { AlertCircle, Phone, Users, CalendarCheck } from 'lucide-react'; // Import icons
import { usePrefetchVideos } from '@/hooks/useVideos'; // Import video prefetch

// Define the structure of the data returned by the Edge Function
interface KpiData {
  callsMade: number;
  callsAnswered: number;
  appointmentsBooked: number;
}
interface OutcomeData { name: string; value: number; }
interface ActivityData { date: string; calls: number; }
interface RecentCallData { id: string; time: string; number: string | null; outcome: string | null; }
interface DashboardApiData {
  kpis: KpiData;
  outcomes: OutcomeData[];
  activity: ActivityData[];
  recentCalls: RecentCallData[];
}

export default function DashboardPage() {
  const { user } = useAuth(); // Get user for auth token
  const [timeRangeOption, setTimeRangeOption] = useState<TimeRangeOption>('last7days');  // Prefetch videos when entering the dashboard
  usePrefetchVideos();

  // Calculate date range based on selected option
  const dateRange = useMemo(() => getDateRange(timeRangeOption), [timeRangeOption]);

  // Fetch dashboard data using react-query
  const { data: dashboardData, isLoading, isError, error } = useQuery<DashboardApiData, Error>({
    // Query key includes date range to refetch when it changes
    queryKey: ['dashboardAnalytics', dateRange.startISO, dateRange.endISO],
    queryFn: async () => {
      // Get session for auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Construct URL with query parameters
      const url = new URL(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dashboard-analytics`);
      url.searchParams.append('startDate', dateRange.startISO);
      url.searchParams.append('endDate', dateRange.endISO);

      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch dashboard data (${response.status})`);
      }
      return response.json();
    },
    enabled: !!user, // Only run query if user is logged in
    refetchOnWindowFocus: false, // Optional: prevent refetch on focus
    staleTime: 5 * 60 * 1000, // Optional: cache data for 5 minutes
  });

  const handleTimeRangeChange = (value: string) => {
    setTimeRangeOption(value as TimeRangeOption);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">Dashboard</h1>
        {/* Time Range Selector */}
        <Select value={timeRangeOption} onValueChange={handleTimeRangeChange}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Select time range" />
          </SelectTrigger>
          <SelectContent>
            {timeRangeOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Error State */}
      {isError && (
         <div className="bg-destructive/10 text-destructive p-4 rounded-md border border-destructive/20 flex items-center gap-2">
            <AlertCircle className="h-5 w-5"/>
           <p>Error loading dashboard data: {error?.message}</p>
         </div>
       )}

      {/* Loading State */}
      {isLoading && <DashboardSkeleton />}

      {/* Data Display */}
      {!isLoading && !isError && dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* KPI Row */}
          <KPICard
              title="Calls Made"
              value={dashboardData.kpis.callsMade}
              icon={<Phone className="h-4 w-4"/>}
              isLoading={isLoading}
           />
          <KPICard
              title="Calls Answered"
              value={dashboardData.kpis.callsAnswered}
              icon={<Users className="h-4 w-4"/>}
              isLoading={isLoading}
           />
          <KPICard
              title="Appointments Booked"
              value={dashboardData.kpis.appointmentsBooked}
              icon={<CalendarCheck className="h-4 w-4"/>}
              isLoading={isLoading}
           />

          {/* Chart Row */}
           <div className="md:col-span-1 lg:col-span-1">
               <OutcomePieChart data={dashboardData.outcomes} isLoading={isLoading} />
           </div>
           <div className="md:col-span-1 lg:col-span-2">
               <ActivityChart data={dashboardData.activity} isLoading={isLoading} />
           </div>


          {/* Recent Activity Row */}
          <div className="md:col-span-2 lg:col-span-3">
            <RecentCallsList data={dashboardData.recentCalls} isLoading={isLoading} />
          </div>
        </div>
      )}
       {!isLoading && !isError && !dashboardData && (
          <div className="text-center py-10 text-muted-foreground">
              No data available for the selected period.
          </div>
       )}
    </div>
  );
}